import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { TranslatableInput } from "./TranslatableInput";
import { LocalizedContent } from "@/types";

interface FooterSettings {
  id: string;
  about_text: LocalizedContent;
  copyright_text: string;
  quick_links: Array<{ label: string; url: string }>;
  contact_info: {
    address?: string;
    email?: string;
    phone?: string;
  };
}

export function FooterManager() {
  const [settings, setSettings] = useState<FooterSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('footer_settings')
        .select('*')
        .eq('is_active', true)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setSettings({
          id: data.id,
          about_text: data.about_text || '',
          copyright_text: data.copyright_text || '',
          quick_links: data.quick_links as Array<{ label: string; url: string }> || [],
          contact_info: data.contact_info as any || {}
        });
      } else {
        // Initialize with default values
        setSettings({
          id: '',
          about_text: '',
          copyright_text: '',
          quick_links: [],
          contact_info: {}
        });
      }
    } catch (error) {
      console.error('Error fetching footer settings:', error);
      toast({
        title: "Error",
        description: "Failed to fetch footer settings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;

    setSaving(true);
    try {
      const { data: profile } = await supabase
        .from('photographer_profiles')
        .select('id')
        .single();

      if (!profile) throw new Error('Photographer profile not found');

      const payload = {
        photographer_id: profile.id,
        about_text: settings.about_text,
        copyright_text: settings.copyright_text,
        quick_links: settings.quick_links,
        contact_info: settings.contact_info,
        is_active: true
      };

      if (settings.id) {
        const { error } = await supabase
          .from('footer_settings')
          .update(payload as any)
          .eq('id', settings.id);

        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('footer_settings')
          .insert([payload] as any)
          .select()
          .single();

        if (error) throw error;
        if (data) setSettings({ ...settings, id: data.id });
      }

      toast({
        title: "Success",
        description: "Footer settings saved successfully",
      });
    } catch (error) {
      console.error('Error saving footer settings:', error);
      toast({
        title: "Error",
        description: "Failed to save footer settings",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const addQuickLink = () => {
    if (!settings) return;
    setSettings({
      ...settings,
      quick_links: [...settings.quick_links, { label: '', url: '' }]
    });
  };

  const updateQuickLink = (index: number, field: 'label' | 'url', value: string) => {
    if (!settings) return;
    const newLinks = [...settings.quick_links];
    newLinks[index] = { ...newLinks[index], [field]: value };
    setSettings({ ...settings, quick_links: newLinks });
  };

  const removeQuickLink = (index: number) => {
    if (!settings) return;
    const newLinks = settings.quick_links.filter((_, i) => i !== index);
    setSettings({ ...settings, quick_links: newLinks });
  };

  if (loading) {
    return <div className="flex justify-center py-8">Loading footer settings...</div>;
  }

  if (!settings) return null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Footer Management</h2>
        <p className="text-muted-foreground">Customize your website footer content</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>About Text</CardTitle>
          <CardDescription>Brief description for the about section</CardDescription>
        </CardHeader>
        <CardContent>
          <TranslatableInput
            label="About Text"
            component="textarea"
            value={settings.about_text}
            onChange={(val) => setSettings({ ...settings, about_text: val })}
            placeholder="Enter about text..."
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Copyright Text</CardTitle>
          <CardDescription>Copyright notice for the footer</CardDescription>
        </CardHeader>
        <CardContent>
          <Input
            value={settings.copyright_text}
            onChange={(e) => setSettings({ ...settings, copyright_text: e.target.value })}
            placeholder="© 2024 Your Name. All rights reserved."
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Quick Links</CardTitle>
          <CardDescription>Add navigation links to the footer</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings.quick_links.map((link, index) => (
            <div key={index} className="flex gap-3 items-start">
              <div className="flex-1 space-y-2">
                <Input
                  value={link.label}
                  onChange={(e) => updateQuickLink(index, 'label', e.target.value)}
                  placeholder="Link Label"
                />
                <Input
                  value={link.url}
                  onChange={(e) => updateQuickLink(index, 'url', e.target.value)}
                  placeholder="/page or https://..."
                />
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => removeQuickLink(index)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button onClick={addQuickLink} variant="outline" className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Add Quick Link
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Contact Information</CardTitle>
          <CardDescription>Additional contact details (optional)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Address</Label>
            <Input
              value={settings.contact_info.address || ''}
              onChange={(e) => setSettings({
                ...settings,
                contact_info: { ...settings.contact_info, address: e.target.value }
              })}
              placeholder="123 Photography St, City, State"
            />
          </div>
          <div>
            <Label>Email</Label>
            <Input
              value={settings.contact_info.email || ''}
              onChange={(e) => setSettings({
                ...settings,
                contact_info: { ...settings.contact_info, email: e.target.value }
              })}
              placeholder="contact@example.com"
            />
          </div>
          <div>
            <Label>Phone</Label>
            <Input
              value={settings.contact_info.phone || ''}
              onChange={(e) => setSettings({
                ...settings,
                contact_info: { ...settings.contact_info, phone: e.target.value }
              })}
              placeholder="+1 (555) 123-4567"
            />
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="w-full">
        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Save Footer Settings
      </Button>
    </div>
  );
}
